import chunk from 'lodash/chunk';

import type { ListFieldsApiResponse } from '~/redux/api/unified-fields/enhancements';
import type { DataField } from '~/types/Field';

import { mapUfClientFieldToDataField } from '~/features/x-ray/fields/mapUfClientFieldToDataField';
import { FlagType } from '~/flags/flags';
import { getFlag } from '~/flags/useFlag';
import { unifiedFields } from '~/services/serviceInstances';

interface Request {
  ids: string[];
}

const UF_FIELD_IDS_CHUNK_SIZE = 40;

const fetchClientFieldsByIds = async (ids: string[]): Promise<DataField[]> => {
  const response = await unifiedFields.get<ListFieldsApiResponse>(
    '/client_fields',
    {
      params: {
        'filter[active]': true,
        'filter[id]': ids.join(','),
        'page[limit]': UF_FIELD_IDS_CHUNK_SIZE,
      },
    },
  );

  const compositeFieldsEnabled = getFlag(FlagType.CustomAiCompositeFields);

  return response.data
    .map((field) =>
      mapUfClientFieldToDataField(field, { compositeFieldsEnabled }),
    )
    .filter((field): field is DataField => field !== null);
};

export const getClientFieldsByIds = async ({
  ids,
}: Request): Promise<{ results: DataField[] }> => {
  const uniqueIds = [...new Set(ids)];

  if (uniqueIds.length <= UF_FIELD_IDS_CHUNK_SIZE) {
    return { results: await fetchClientFieldsByIds(uniqueIds) };
  }

  const responses = await Promise.all(
    chunk(uniqueIds, UF_FIELD_IDS_CHUNK_SIZE).map(fetchClientFieldsByIds),
  );

  return { results: responses.flat() };
};
